/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 31 Milk St # 960789 Boston, MA 02196 USA.
 *
 * Copyright (c) 2019-2026 (original work) Open Assessment Technologies SA
 *
 */

export const DEFAULT_LOCALE = 'en-US';

const translations = {
    [DEFAULT_LOCALE]: {
        'pages.Login.welcome': 'Welcome to the Diagnostic Tool',
        'pages.Login.welcome2': 'Your system will undergo a test to ensure it is ready for any session.',
        'pages.Login.your_ip': 'Your IP address',
        'pages.Login.loading': 'Loading...',
        'pages.Login.school_name': 'School name',
        'pages.Login.enter_school_name': 'Enter your school name',
        'pages.Login.workstation_name': 'Workstation name',
        'pages.Login.enter_workstation_name': 'Enter your workstation name',
        'pages.Login.country': 'Your current country',
        'pages.Login.enter_country': 'Enter your country',
        'pages.Login.enter': 'Enter',
        'pages.Login.is_audio': 'Do you also want to run an audio capability test?',
        'pages.Login.is_video': 'Do you also want to run a video capability test?',
        'pages.Login.no': 'No',
        'pages.Login.yes': 'Yes',
        'pages.Login.your_name': 'Your name',
        'pages.Login.enter_your_name': 'Enter your name or a name to identify this diagnostic test',
        'pages.diagnostic.AudioCapability.select_mic': 'Select a microphone',
        'pages.diagnostic.AudioCapability.input_level': 'Input level',
        'pages.diagnostic.AudioCapability.output_level': 'Output level',
        'pages.diagnostic.AudioCapability.recording_test': 'To test your microphone and speaker please begin by recording a short audio. Once complete you can playback your recording.',
        'pages.diagnostic.AudioCapability.continue_to_syscheck': 'Continue to system check',
        'pages.diagnostic.AudioCapability.continue_to_video_check': 'Continue to video check',
        'pages.diagnostic.AudioCapability.was_playback_clear': 'Was the playback of your recording clear?',
        'pages.diagnostic.AudioCapability.unable_access_mic': 'Unable to access the microphone',
        'pages.diagnostic.AudioCapability.access_mic_required': 'Access to your microphone is required to test your audio. Please allow microphone access through your browser\'s settings.',
        'pages.diagnostic.AudioCapability.diagnostic_tool': 'Diagnostic tool',
        'pages.diagnostic.AudioCapability.step_of': 'Step {{step}} of {{of}}: ',
        'pages.diagnostic.AudioCapability.audio_check': 'Audio check',
        'pages.diagnostic.AudioCapability.no': 'No',
        'pages.diagnostic.AudioCapability.yes': 'Yes',
        'pages.diagnostic.AudioCapability.mic': 'microphone',
        'pages.diagnostic.VideoCapability.diagnostic_tool': 'Diagnostic tool',
        'pages.diagnostic.VideoCapability.step_of': 'Step {{step}} of {{of}}: ',
        'pages.diagnostic.VideoCapability.video_check': 'Video check',
        'pages.diagnostic.VideoCapability.recording_test': 'To test your camera and microphone please record a short video. Once complete you can playback your recording.',
        'pages.diagnostic.VideoCapability.continue_to_syscheck': 'Continue to system check',
        'pages.diagnostic.VideoCapability.live_preview': 'Live preview',
        'pages.diagnostic.VideoCapability.preview': 'Recorded preview',
        'pages.diagnostic.VideoCapability.was_playback_clear': 'Was the playback of your recording clear?',
        'pages.diagnostic.VideoCapability.unable_access_media': 'Unable to access the camera or microphone',
        'pages.diagnostic.VideoCapability.access_media_required': 'Access to your camera and microphone is required to test your video. Please allow access through your browser settings.',
        'pages.diagnostic.VideoCapability.start_recording': 'Start recording',
        'pages.diagnostic.VideoCapability.stop_recording': 'Stop recording',
        'pages.diagnostic.VideoCapability.record_again': 'Record again',
        'pages.diagnostic.VideoCapability.yes': 'Yes',
        'pages.diagnostic.VideoCapability.no': 'No',
        'pages.diagnostic.SystemCompatibility.diagnostic_tool': 'Diagnostic tool',
        'pages.diagnostic.SystemCompatibility.system_check': 'System Compatibility Check',
        'pages.diagnostic.SystemCompatibility.check_browser': 'Checking browser compatibility... ({{progress}}%)',
        'pages.diagnostic.SystemCompatibility.check_secure_browser': 'Checking secure browser compatibility... ({{progress}}%)',
        'pages.diagnostic.SystemCompatibility.check_connection': 'Checking connection compatibility... ({{progress}}%)',
        'pages.diagnostic.SystemCompatibility.check_audio': 'Checking audio compatibility... ({{progress}}%)',
        'pages.diagnostic.SystemCompatibility.check_video': 'Checking video compatibility... ({{progress}}%)',
        'pages.diagnostic.SystemCompatibility.check_system': 'Checking system compatibility... ({{progress}}%)',
        'pages.diagnostic.SystemCompatibility.step_of': 'Step {{step}} of {{of}}: ',
        'pages.diagnostic.SystemCompatibility.no_internet_title': 'No Internet Connection',
        'pages.diagnostic.SystemCompatibility.no_internet_remediation': 'Please check your Internet connection and try again',
        'pages.diagnostic.SaveResults.progress': 'Saving diagnostic results',
        'pages.diagnostic.SaveResults.retry_save_heading': 'Error! We could not save diagnostic results.',
        'pages.diagnostic.SaveResults.retry_save_message': 'Please, try again. If the problem persists, contact the support service.',
        'pages.diagnostic.SaveResults.cancel_button': 'Cancel',
        'pages.diagnostic.SaveResults.retry_save_button': 'Try again',
        'pages.diagnostic.SaveResults.save_error': 'We failed to save diagnostic data. Please, contact the support service.',
        'pages.diagnostic.SaveResults.saving_failure_title': 'Failed to save diagnostic data.',
        'pages.diagnostic.SaveResults.saving_failure_remediation': 'Please, contact the support service.',
        'pages.diagnostic.Results.retest': 'Retest system',
        'pages.diagnostic.Results.diagnostic_tool': 'Diagnostic tool',
        'pages.diagnostic.Results.results': 'Results',
        'pages.diagnostic.Results.bandwidth': 'Bandwidth',
        'pages.diagnostic.Results.download': 'Download',
        'pages.diagnostic.Results.upload': 'Upload',
        'pages.diagnostic.Results.latency': 'Latency',
        'pages.diagnostic.Results.bandwidth_result_based': 'The bandwidth pass or fail result is based on supporting {{d}} concurrent test takers.',
        'pages.diagnostic.Results.browser': 'Browser',
        'pages.diagnostic.Results.secure_browser': 'Secure Browser',
        'pages.diagnostic.Results.secure_browser_minimum_version': 'Minimum version met',
        'pages.diagnostic.Results.secure_browser_neutral': 'n/a',
        'pages.diagnostic.Results.secure_browser_version_ok': 'Pass',
        'pages.diagnostic.Results.secure_browser_version_ko': 'Fail',
        'pages.diagnostic.Results.secure_browser_process': 'Prohibited apps running',
        'pages.diagnostic.Results.secure_browser_process_ok': 'Not detected',
        'pages.diagnostic.Results.secure_browser_process_ko': 'Detected',
        'pages.diagnostic.Results.secure_browser_partial_msg': 'Your browser is compatible for non-secure sessions. To verify readiness for secure sessions, relaunch this tool in TAO Secure Browser.',
        'pages.diagnostic.Results.secure_browser_partial_btn': 'Download TAO Secure Browser',
        'pages.diagnostic.Results.secure_browser_version_error': 'Update here. <a href="{{d}}">TAO Secure Browser</a>',
        'pages.diagnostic.Results.secure_browser_process_list_error': 'Detected apps: {{d}}. Exit the secure browser, quit these apps, then relaunch.',
        'pages.diagnostic.Results.version': 'Version',
        'pages.diagnostic.Results.audio': 'Audio',
        'pages.diagnostic.Results.video': 'Video',
        'pages.diagnostic.Results.audio_bandwidth': 'Audio bandwidth',
        'pages.diagnostic.Results.video_bandwidth': 'Video bandwidth',
        'pages.diagnostic.Results.playback': 'Playback',
        'pages.diagnostic.Results.system_meets_specs': 'Your system meets the technical specifications',
        'pages.diagnostic.Results.system_not_meet_specs': 'Your system does not meet the technical specifications and is not suited to run this assessment.',
        'pages.diagnostic.Results.connectivity': 'Connectivity',
        'pages.diagnostic.Results.webSocketConnection': 'WebSocket',
        'pages.diagnostic.WebSocketCompatibility.check_connection': 'Checking WebSocket connection... ({{progress}}%)',
        'components.AudioCheckResultItem.yes': 'Yes',
        'components.AudioCheckResultItem.no': 'No',
        'components.BrowserCheckResultItem.unsupported': 'Unsupported',
        'components.WebSocketCheckResultItem.yes': 'Connected',
        'components.WebSocketCheckResultItem.no': 'Not connected'
    }
}

export function getDefaultTranslations() {
    return { [DEFAULT_LOCALE]: translations[DEFAULT_LOCALE] };
}
